import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, TemplateRef, ChangeDetectionStrategy, signal, computed, ViewContainerRef, OnDestroy, forwardRef, HostListener, ViewChildren, QueryList, Directive, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Overlay, OverlayRef, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { ActiveDescendantKeyManager, Highlightable } from '@angular/cdk/a11y';
import { Subject, takeUntil } from 'rxjs';

// --- TYPE INTERNE POUR L'OPTIMISATION ---
// Permet de stocker le label calculé et la valeur extraite une seule fois
export type OptionViewModel = {
  label: string;
  value: any;
  raw: any;
  disabled: boolean;
};

/**
 * DIRECTIVE INTERNE POUR LES OPTIONS
 */
@Directive({
  selector: '[smartOption]',
  standalone: true,
  exportAs: 'smartOption'
})
export class SmartOptionDirective implements Highlightable {
  @Input({ required: true }) item: any;

  private _isActive = signal(false);
  isActive = this._isActive.asReadonly();

  constructor(public element: ElementRef) { }

  setActiveStyles(): void {
    this._isActive.set(true);
    this.element.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  setInactiveStyles(): void {
    this._isActive.set(false);
  }

  getLabel(): string {
    return this.element.nativeElement.textContent.trim();
  }
}

/**
 * COMPOSANT PRINCIPAL SMART SELECT OPTIMISÉ
 */
@Component({
  selector: 'smart-select',
  standalone: true,
  imports: [
    CommonModule,
    OverlayModule,
    PortalModule,
    FormsModule,
    ReactiveFormsModule,
    SmartOptionDirective
  ],
  templateUrl: './smart-select.component.html',
  styleUrls: ['./smart-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SmartSelectComponent),
    multi: true
  }]
})
export class SmartSelectComponent implements ControlValueAccessor, OnDestroy, AfterViewInit {

  // --- GESTION DES INPUTS VIA SIGNAL ---
  // On utilise un signal privé pour la source de données afin que 'computed' puisse réagir
  private _items = signal<any[]>([]);

  @Input({ required: true })
  set items(value: any[]) {
    this._items.set(value || []);
  }
  // Getter pour compatibilité si besoin d'accéder à la liste brute
  get items(): any[] { return this._items(); }

  @Input() bindLabel: string = 'label';
  @Input() bindLabelFn: ((item: any) => string) | null = null;  // Fonction personnalisée pour construire le label
  @Input() bindValue: string | null = null;
  @Input() bindDisabled: string | ((item: any) => boolean) = 'disabled';  // On accepte soit une string (nom de la clé), soit une fonction
  @Input() itemTemplate: TemplateRef<any> | null = null;
  @Input() placeholder = 'Sélectionner...';
  @Input() disabled = false;
  @Input() multiple = false;
  @Input() compareWith: (o1: any, o2: any) => boolean = (o1, o2) => o1 === o2;
  @Input() maxDisplayedSelections = 3;
  @Input() width: string = '100%';  // Largeur du wrapper (ex: '100%', '250px', 'auto')
  @Input() height: string = '1.75rem';  // Hauteur de l'input (ex: '40px', '48px', '3rem')
  @Input() optionHeight: string = '1.875rem';  // Hauteur de l'input (ex: '40px', '48px', '3rem')
  @Input() singleLineOption = true;  // Si true, les options du panel sont sur une seule ligne avec ellipsis
  @Input() selectable: boolean = true;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() openedChange = new EventEmitter<boolean>();

  @ViewChild('panel') panelTemplate!: TemplateRef<any>;
  @ViewChild('trigger') triggerElement!: ElementRef<HTMLElement>;
  @ViewChildren(SmartOptionDirective) options!: QueryList<SmartOptionDirective>;

  readonly isOpen = signal(false);
  readonly selectedValues = signal<any[]>([]);

  // --- OPTIMISATION 1 : Transformation des données (O(N) une seule fois) ---
  // Transforme la liste brute en ViewModel prêt à afficher.
  // Recalculé uniquement si _items() change.
  readonly viewModels = computed<OptionViewModel[]>(() => {
    return this._items().map(item => ({
      label: this.getLabel(item),
      value: this.getValue(item),
      raw: item,
      disabled: this.getDisabled(item)
    }));
  });

  // --- OPTIMISATION 2 : Map pour accès O(1) ---
  // Permet de retrouver le label d'une valeur sans refaire de .find() ou .filter()
  readonly itemsMap = computed(() => {
    const map = new Map<any, string>();
    this.viewModels().forEach(vm => {
      // On utilise vm.value comme clé pour retrouver vm.label rapidement
      map.set(vm.value, vm.label);
    });
    return map;
  });

  // --- OPTIMISATION 3 : Affichage optimisé ---
  readonly displayValue = computed(() => {
    const values = this.selectedValues();
    if (!values || values.length === 0) return '';

    const vms = this.viewModels();

    // Utilise compareWith pour tolérer les écarts de type (ex: number vs string)
    const labels = values
      .map(v => vms.find(vm => this.compareWith(v, vm.value))?.label)
      .filter(l => l !== undefined) as string[];

    if (labels.length === 0) return '';

    if (!this.multiple) return labels[0];

    const visibleLabels = labels.slice(0, this.maxDisplayedSelections);
    if (labels.length > this.maxDisplayedSelections) {
      visibleLabels.push(`+${labels.length - this.maxDisplayedSelections}`);
    }
    return visibleLabels.join(', ');
  });

  public onTouched: () => void = () => { };
  private onChange: (value: any) => void = () => { };
  private keyManager!: ActiveDescendantKeyManager<SmartOptionDirective>;
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal<any> | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) { }

  ngOnInit(): void {
    // console.log('this.bindValue', this.bindValue);
    // console.log('this.items', this.items);
  }

  ngAfterViewInit(): void {
    this.keyManager = new ActiveDescendantKeyManager(this.options)
      .withWrap()
      .withVerticalOrientation()
      .withTypeAhead();

    this.options.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.isOpen()) {
        setTimeout(() => this.syncActiveItem(), 0);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.overlayRef?.dispose();
  }

  // --- GESTION CLAVIER ---

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (!this.isOpen()) {
      if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === ' ') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    if (event.key === 'Escape' || event.key === 'Tab') {
      this.close(true);
    } else if (!this.selectable) {
      return;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.keyManager.activeItem) {
        // Attention : keyManager.activeItem.item contient l'objet raw (voir HTML)
        this.onItemClick(this.keyManager.activeItem.item);
      }
    } else {
      this.keyManager.onKeydown(event);
    }
  }

  // --- ACTIONS ---

  toggle(): void {
    if (this.disabled) return;
    this.isOpen() ? this.close(true) : this.open();
  }

  open(): void {
    if (this.isOpen() || this.disabled) return;

    this.createOverlay();

    // Ajustement largeur
    const triggerWidth = this.triggerElement.nativeElement.getBoundingClientRect().width;
    if (this.overlayRef) {
      this.overlayRef.updateSize({ width: triggerWidth });
    }

    this.isOpen.set(true);
    this.openedChange.emit(true);

    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.portal = new TemplatePortal(this.panelTemplate, this.viewContainerRef);
      this.overlayRef.attach(this.portal);
    }

    this.overlayRef!.outsidePointerEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const trigger = this.triggerElement.nativeElement;

        if (trigger.contains(target)) {
          return;
        }

        this.close(false);
      });
  }

  close(restoreFocus: boolean = false): void {
    if (!this.isOpen()) return;

    this.isOpen.set(false);
    this.openedChange.emit(false);
    this.overlayRef?.detach();
    this.onTouched();

    if (restoreFocus) {
      this.triggerElement.nativeElement.focus();
    }
  }

  // --- LOGIQUE METIER & HELPERS ---

  // Ces fonctions servent maintenant principalement à la construction du ViewModel
  getLabel(item: any): string {
    if (item === null || item === undefined) return '';

    // Si une fonction personnalisée est fournie, on l'utilise en priorité
    if (this.bindLabelFn) {
      return this.bindLabelFn(item);
    }

    // Sinon, on utilise le comportement par défaut avec bindLabel
    return typeof item === 'object' ? item[this.bindLabel] : String(item);
  }

  getValue(item: any): any {
    if (item === null || item === undefined) return null;
    return (typeof item === 'object' && this.bindValue) ? item[this.bindValue] : item;
  }

  getDisabled(item: any): boolean {
    if (item === null || item === undefined) return false;

    // CAS 1 : C'est une fonction -> On l'exécute avec l'item
    if (typeof this.bindDisabled === 'function') {
      return this.bindDisabled(item);
    }

    // CAS 2 : C'est une string -> On cherche la propriété dans l'objet
    if (typeof this.bindDisabled === 'string') {
      return typeof item === 'object' ? !!item[this.bindDisabled] : false;
    }

    return false;
  }

  isSelected(item: any): boolean {
    const val = this.getValue(item);
    return this.selectedValues().some(v => this.compareWith(v, val));
  }

  // item ici est l'objet "raw"
  onItemClick(item: any): void {
    // console.log('item',item);
    if (!this.selectable) return;
    if (this.getDisabled(item)) return;  // si disabled on part
    const value = this.getValue(item);
    // console.log('value',value);

    if (this.multiple) {  // multiple signifie que l'on peux choisir plusieur elements
      const current = this.selectedValues();
      const isAlreadySelected = current.some(v => this.compareWith(v, value));
      const next = isAlreadySelected
        ? current.filter(v => !this.compareWith(v, value))
        : [...current, value];

      this.selectedValues.set(next);
      this.onChange(next);
      this.selectionChange.emit(next);
    } else {
      this.selectedValues.set([value]);
      this.onChange(value);
      this.selectionChange.emit(value);
      this.close(true);
    }
  }

  private createOverlay(): void {
    if (this.overlayRef) return;

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.triggerElement)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
      ])
      .withFlexibleDimensions(false)
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false
    });
  }

  private syncActiveItem(): void {
    if (!this.keyManager) return;

    const selected = this.selectedValues()[0];
    if (selected !== undefined) {
      // On peut maintenant chercher dans viewModels si on le souhaite, 
      // mais l'index est le même que dans _items()
      const index = this._items().findIndex(item => this.compareWith(this.getValue(item), selected));
      if (index > -1) {
        this.keyManager.setActiveItem(index);
        return;
      }
    }
    this.keyManager.setFirstItemActive();
  }

  // --- CONTROL VALUE ACCESSOR ---

  writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.selectedValues.set([]);
    } else {
      const valArray = this.multiple && Array.isArray(value) ? value : [value];
      this.selectedValues.set(valArray);
    }
  }

  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}